// src/pages/Login.jsx - Apartment Management System
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiUserLine, RiBuildingLine, RiLoginBoxLine, RiErrorWarningLine, RiShieldKeyholeLine } from 'react-icons/ri';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { loginUser, verifyOTP, resendOTP } from '../models/authModel';
import Navbar from './home/Navbar';

const Login = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('login'); // 'login' or 'otp'
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState('');

  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });

    // Clear errors when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Clear login error when typing
    if (loginError) {
      setLoginError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!credentials.username.trim()) {
      newErrors.username = t('username_required') || 'Username is required';
    }

    if (!credentials.password) {
      newErrors.password = t('password_required') || 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await loginUser(credentials.username, credentials.password);

      if (response.success) {
        if (response.requireOtp) {
          setStep('otp');
          setUserId(response.userId);
          setLoginError(''); // Clear any previous errors
          // No navigation needed, step change handles UI
        } else if (response.token) {
          // Use AuthContext login method
          login(response.user, response.token);

          // Redirect to dashboard or intended page
          const redirectTo = location.state?.from?.pathname || '/admin/dashboard';
          navigate(redirectTo);
        } else {
          setLoginError(response.message || t('login_failed') || 'Login failed');
        }
      } else {
        setLoginError(response.message || t('login_failed') || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();

    if (!otp || otp.length < 6) {
      setLoginError(t('otp_invalid_code') || 'Please enter a valid verification code');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await verifyOTP(userId, otp);

      if (response.success) {
        login(response.user, response.token);
        const redirectTo = location.state?.from?.pathname || '/admin/dashboard';
        navigate(redirectTo);
      } else {
        setLoginError(response.message || 'Verification failed');
      }
    } catch (error) {
      console.error('OTP error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setLoginError('');
    try {
      // userId must be set from initial login response
      await resendOTP(userId);
      setLoginError(t('otp_resent') || 'Verification code resent to your WhatsApp');
      // Ideally utilize a success state/toast but using error box for now as feedback
    } catch (error) {
      console.error('Resend error:', error);
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleError = (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        setLoginError(t('invalid_credentials') || 'Invalid username or password');
      } else if (error.response.status === 403) {
        setLoginError(t('account_deactivated') || 'Your account has been deactivated');
      } else {
        setLoginError(error.response.data?.message || t('login_failed') || 'Login failed');
      }
    } else if (error.request) {
      setLoginError(t('server_no_response') || 'No response from server. Please try again later.');
    } else {
      setLoginError(error.message || t('login_failed') || 'Login failed');
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-zinc-950' : 'bg-white'}`}>
      <Navbar />
      <div className={`max-w-md w-full relative z-10 ${isDarkMode ? 'bg-zinc-900 border-white/[0.06]' : 'bg-white border-black/[0.1]'} border rounded-xl shadow-2xl overflow-hidden transition-all duration-300`}>
        <div className="p-6 sm:p-8">
          <div className="text-center mb-8">
            <div className="mb-4">
              <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="QuadCores Logo"
                className="h-16 w-auto mx-auto"
              />
            </div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {step === 'otp' ? (t('otp_security_verification') || 'Security Verification') : 'Quadcores Properties'}
            </h1>
            <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
              {step === 'otp'
                ? (t('otp_enter_code_whatsapp') || 'Please enter the verification code sent to your WhatsApp.')
                : (t('login_to_continue') || 'Sign in to your account to continue')}
            </p>
          </div>

          {loginError && (
            <div className={`mb-6 p-3 rounded-lg flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-100 text-red-700'
              }`}>
              <RiErrorWarningLine className="mr-2 flex-shrink-0" />
              <span>{loginError}</span>
            </div>
          )}

          {step === 'login' ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('username') || 'Username'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                    type="text"
                    name="username"
                    value={credentials.username}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                      ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                      : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                      } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.username ? 'border-red-500' : ''
                      }`}
                    placeholder={t('enter_username') || 'Enter your username'}
                  />
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.username}
                  </p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('password') || 'Password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={credentials.password}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 ${isDarkMode
                      ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                      : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                      } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500 ${errors.password ? 'border-red-500' : ''
                      }`}
                    placeholder={t('enter_password') || 'Enter your password'}
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 px-4 ${isDarkMode
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-red-600 hover:bg-red-700'
                    } text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'
                    } flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('logging_in') || 'Signing in'}...
                    </>
                  ) : (
                    <>
                      <RiLoginBoxLine className="mr-2" />
                      {t('login') || 'Sign In'}
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleOtpSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('otp_verification_code') || 'Verification Code'}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className={`block w-full px-3 py-3 text-center text-2xl tracking-widest ${isDarkMode
                      ? 'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500'
                      : 'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'
                      } border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`}
                    placeholder="123456"
                    maxLength={6}
                  />
                </div>
                <p className={`mt-2 text-sm text-center ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                  {t('otp_check_whatsapp') || 'Check your WhatsApp for the code'}
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 px-4 ${isDarkMode
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-red-600 hover:bg-red-700'
                    } text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${isDarkMode ? 'focus:ring-offset-gray-900' : 'focus:ring-offset-white'
                    } flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('otp_verifying') || 'Verifying...'}
                    </>
                  ) : (
                    <>
                      <RiShieldKeyholeLine className="mr-2" />
                      {t('otp_verify_code') || 'Verify Code'}
                    </>
                  )}
                </button>
              </div>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className={`text-sm font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}
                >
                  {t('otp_resend_code') || 'Resend Code'}
                </button>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep('login')}
                    className={`text-sm ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-700 hover:text-zinc-950'}`}
                  >
                    {t('otp_back_to_login') || 'Back to Login'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {step === 'login' && (
            <div className="text-center mt-6">
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
                {t('dont_have_account') || "Don't have an account?"} {' '}
                <Link to="/register" className={`font-medium ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-700 hover:text-red-600'}`}>
                  {t('register') || 'Create Account'}
                </Link>
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default Login;